import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from '../Context/AuthContext';
import ChatService from './ChatService';

const ChatContext = createContext();

export const useChat = () => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error('useChat must be used within a ChatProvider');
  }
  return context;
};

export const ChatProvider = ({ children }) => {
  const [chats, setChats] = useState([]);
  const [totalUnreadCount, setTotalUnreadCount] = useState(0);
  const [activeChatId, setActiveChatId] = useState(null);
  const { user } = useAuth();

  const normalizeProjectId = (id) => {
    if (typeof id === 'string') {
      return id.trim();
    }
    return id || '';
  };

  // Subscribe to all user chats
  useEffect(() => {
    if (!user) {
      setChats([]);
      setTotalUnreadCount(0);
      return;
    }

    const unsubscribe = ChatService.subscribeToUserChats(
      user.uid,
      'brand',
      (userChats) => {
        const normalizedChats = userChats.map(chat => ({
          ...chat,
          projectId: normalizeProjectId(chat.projectId),
          normalizedProjectId: normalizeProjectId(chat.projectId)
        }));
        setChats(normalizedChats);
        
        // Calculate total unread count
        const unreadCount = normalizedChats.reduce((total, chat) => {
          return total + (chat.unreadCount?.brand || 0);
        }, 0);
        setTotalUnreadCount(unreadCount);
      }
    );

    return () => unsubscribe();
  }, [user]);

  const getChatForProject = (projectId) => {
    const targetProjectId = normalizeProjectId(projectId);
    return chats.find(chat => normalizeProjectId(chat.projectId) === targetProjectId);
  };

  /**
   * Get unread count for a specific chat
   */
  const getUnreadCount = (chatId) => {
    const chat = chats.find(c => c.chatId === chatId);
    return chat?.unreadCount?.brand || 0;
  };

  /**
   * Check if a chat exists for a brief
   */
  const hasChatForProject = (projectId) => {
    const targetProjectId = normalizeProjectId(projectId);
    return chats.some(chat => normalizeProjectId(chat.projectId) === targetProjectId);
  };

  /**
   * Create a new chat
   */
  const createChat = async (chatData) => {
    try {
      const chatId = await ChatService.createChat(chatData);
      return chatId;
    } catch (error) {
      console.error('Error creating chat:', error);
      throw error;
    }
  };

  /**
   * Close a chat
   */
  const closeChat = async (chatId) => {
    try {
      await ChatService.closeChat(chatId);
    } catch (error) {
      console.error('Error closing chat:', error);
      throw error;
    }
  };

  const value = {
    chats,
    totalUnreadCount,
    activeChatId,
    setActiveChatId,
    getChatForProject,
    getUnreadCount,
    hasChatForProject,
    createChat,
    closeChat
  };

  return (
    <ChatContext.Provider value={value}>
      {children}
    </ChatContext.Provider>
  );
};

